import Loading from "components/common/Loading";
import OrderCard from "components/common/OrderCard";
import ToolBar from "components/user/ToolBar";
import api from "config/api";
import { addDays, format, subMonths } from "date-fns";
import { useAppSelector } from "hooks/store";
import { useEffect, useState } from "react";
import { OrderItem } from "utils/types";

const MyOrder = () => {
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const { userData, taxes, companyData } = useAppSelector(({ main }) => main);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userData?.clientId) {
      const getOrderDateValue = (order: any) => {
        const raw = String(order?.onDateTime || order?.onDate || "").trim();
        const parsed = raw ? new Date(raw) : null;
        return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
      };

      const getSearchRange = (customerOrders: OrderItem[]) => {
        const dates = customerOrders
          .map(getOrderDateValue)
          .filter(Boolean) as Date[];

        if (!dates.length) {
          return {
            fromDate: format(subMonths(new Date(), 6), "MM/d/yyyy"),
            toDate: format(addDays(new Date(), 1), "MM/d/yyyy"),
          };
        }

        const timestamps = dates.map((date) => date.getTime());

        return {
          fromDate: format(addDays(new Date(Math.min(...timestamps)), -1), "MM/d/yyyy"),
          toDate: format(addDays(new Date(Math.max(...timestamps)), 1), "MM/d/yyyy"),
        };
      };

      const mergeAdminOrderDetails = (
        customerOrders: OrderItem[],
        adminOrders: OrderItem[],
      ) => {
        const adminByOrderId = new Map(
          adminOrders.map((order: any) => [Number(order?.orderID || 0), order]),
        );

        return customerOrders.map((order: any) => {
          const adminOrder: any = adminByOrderId.get(Number(order?.orderID || 0));
          if (!adminOrder) return order;

          return {
            ...order,
            ...adminOrder,
            customer: adminOrder.customer || order.customer,
            orderDetail: Array.isArray(adminOrder.orderDetail) && adminOrder.orderDetail.length
              ? adminOrder.orderDetail
              : order.orderDetail,
          };
        });
      };

      setLoading(true);
      api
        .get(`/api/OrderSubmition/GetOrders?customerID=${userData.clientId}`)
        .then(async ({ data }) => {
          const customerOrders = Array.isArray(data) ? data : [];
          const { fromDate, toDate } = getSearchRange(customerOrders);

          try {
            const { data: adminData } = await api.get(
              `/api/OrderSubmition/GetSearchOrders?fromDate=${fromDate}&toDate=${toDate}`,
            );
            const adminOrders = Array.isArray(adminData) ? adminData : [];
            setOrders(mergeAdminOrderDetails(customerOrders, adminOrders));
          } catch {
            setOrders(customerOrders);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [userData]);

  return (
    <div className="flex-1 max-h-[calc(100vh-50px)] overflow-y-scroll">
      <div className="max-w-screen-3xl 3xl:mx-auto min-h-screen">
        <ToolBar />
        {loading && <Loading />}
        <div className="px-3 pt-3">
          <div className="flex items-center ml-3">
            <h1 className="text-2xl font-bold">My All Orders</h1>
          </div>
          <div className="max-h-[calc(100vh-115px)] overflow-y-auto mt-2 flex flex-col gap-2 no-scrollbar">
            {orders.map((item) => (
              <OrderCard
                {...item}
                taxData={taxes}
                timeZone={companyData?.timeZone || "Pacific Standard Time"}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyOrder;
